import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const TRUST_PILLARS = [
  { icon: 'fa-user-doctor', label: 'Gynaecologist-Led',     sub: 'Built by Dr. Vikram Dukle · 15+ years' },
  { icon: 'fa-shield-halved', label: 'Complete Privacy',     sub: 'Encrypted · No judgement · Anonymous' },
  { icon: 'fa-comments',    label: '24/7 VIVI AI Coach',     sub: 'Ask anything · 14 languages' },
  { icon: 'fa-clipboard-check', label: '16 Clinical Assessments', sub: 'Know your wellness score' },
  { icon: 'fa-book-open',   label: 'Curated Library',        sub: 'Doctor-approved guidance' },
  { icon: 'fa-heart',       label: 'Free Early Access',      sub: 'No payment needed · Open access' },
];

const STEPS = ['account', 'otp', 'profile'];

export default function Auth() {
  const [step, setStep] = useState('account');
  const [mode, setMode] = useState('login'); // login | register
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', dob: '' });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showTrust, setShowTrust] = useState(true);
  const [installEvt, setInstallEvt] = useState(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Capture PWA install prompt (Android/Chrome)
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installEvt) {
      toast.info('Tap your browser menu → "Add to Home screen" to install INTIVIV');
      return;
    }
    installEvt.prompt();
    const choice = await installEvt.userChoice;
    if (choice.outcome === 'accepted') toast.success('INTIVIV added to your home screen');
    setInstallEvt(null);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/home');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.name, form.password, form.dob, form.phone);
      localStorage.setItem('intiviv_onboarding_done', '0');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px', position: 'relative', overflow: 'hidden' }}>
      {/* Background glow */}
      <div style={{ position: 'absolute', top: '-200px', left: '50%', transform: 'translateX(-50%)', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="auth-wrap"
        style={{ width: '100%', position: 'relative', zIndex: 1 }}
      >
        <div className="auth-brand-col">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img
            src="/intiviv-logo.png?v=4"
            alt="INTIVIV"
            style={{
              width: 200,
              maxWidth: '60vw',
              height: 'auto',
              display: 'block',
              margin: '0 auto 14px',
              filter: 'drop-shadow(0 0 28px rgba(212,175,55,0.45)) drop-shadow(0 6px 18px rgba(0,0,0,0.4))',
            }}
            data-testid="auth-brand-mark"
          />
          <div style={{
            fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '1.01rem',
            lineHeight: 1.4,
            background: 'linear-gradient(90deg, var(--gold) 0%, #FFE9A6 25%, var(--gold) 50%, #FFE9A6 75%, var(--gold) 100%)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmerSlide 6s linear infinite',
          }}>
            India's First Gynaecologist-Led<br />AI Intimate Wellness Ecosystem
          </div>
        </div>

        {/* TRUST SECTION — why join INTIVIV (collapsible) */}
        <AnimatePresence initial={false}>
          {showTrust && (
            <motion.div
              key="trust"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35 }}
              style={{ overflow: 'hidden' }}
            >
              <div className="auth-trust" data-testid="auth-trust">
                <div className="auth-trust-eyebrow">
                  <span>Why join INTIVIV</span>
                </div>

                <div className="auth-trust-grid">
                  {TRUST_PILLARS.map((p) => (
                    <div key={p.label} className="auth-trust-card" data-testid={`auth-trust-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="auth-trust-icon">
                        <i className={`fa-solid ${p.icon}`} />
                      </div>
                      <div className="auth-trust-body">
                        <div className="auth-trust-label">{p.label}</div>
                        <div className="auth-trust-sub">{p.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Trust badges row */}
                <div className="auth-trust-badges">
                  <div className="auth-trust-badge"><i className="fa-solid fa-lock" /> End-to-End Encrypted</div>
                  <div className="auth-trust-badge"><i className="fa-solid fa-circle-check" /> Doctor-Approved</div>
                  <div className="auth-trust-badge"><i className="fa-solid fa-flag" /> Made in India</div>
                </div>

                {/* Install as App CTA */}
                <button
                  type="button"
                  className="auth-install-cta"
                  onClick={handleInstall}
                  data-testid="auth-install-pwa"
                >
                  <i className="fa-solid fa-mobile-screen-button" />
                  Install INTIVIV as an app
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowTrust(false)}
                className="auth-trust-collapse"
                data-testid="auth-trust-collapse"
              >
                <i className="fa-solid fa-chevron-down" /> Skip · Continue to sign in
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!showTrust && (
          <button
            type="button"
            onClick={() => setShowTrust(true)}
            className="auth-trust-expand"
            data-testid="auth-trust-expand"
          >
            <i className="fa-solid fa-circle-info" /> Why join INTIVIV?
          </button>
        )}
        </div>

        <div className="auth-form-col">
        {/* Mode toggle */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-soft)', marginBottom: 28 }}>
          {['login','register'].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: '10px 0', fontSize: '0.81rem', fontWeight: 700,
              letterSpacing: '0.15em', textTransform: 'uppercase', background: 'none', border: 'none',
              borderBottom: mode === m ? '2px solid var(--gold)' : '2px solid transparent',
              color: mode === m ? 'var(--gold)' : 'var(--text-dim)',
              cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'var(--font-ui)',
            }}>
              {m === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {mode === 'login' ? (
            <motion.form key="login" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }} onSubmit={handleLogin}>
              <div className="mb-16">
                <label className="input-label">Email</label>
                <input className="input-field" type="email" placeholder="your@email.com" value={form.email} onChange={e => set('email', e.target.value)} required />
              </div>
              <div className="mb-20">
                <label className="input-label">Password</label>
                <div className="pwd-wrap">
                  <input
                    className="input-field pwd-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPwd(s => !s)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-login"
                  >
                    <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="login-submit">
                {loading ? <span className="gold-loader" style={{ width: 18, height: 18 }} /> : 'SIGN IN'}
              </button>
            </motion.form>
          ) : (
            <motion.form key="register" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} onSubmit={handleRegister}>
              <div className="mb-16">
                <label className="input-label">Full Name</label>
                <input className="input-field" type="text" placeholder="Your name" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="mb-16">
                <label className="input-label">Email</label>
                <input className="input-field" type="email" placeholder="your@email.com" value={form.email} onChange={e => set('email', e.target.value)} required />
              </div>
              <div className="mb-16">
                <label className="input-label">Phone Number</label>
                <input className="input-field" type="tel" placeholder="+91 9876543210" value={form.phone} onChange={e => set('phone', e.target.value)} required pattern="[+0-9 ]{8,16}" />
              </div>
              <div className="mb-20">
                <label className="input-label">Password</label>
                <div className="pwd-wrap">
                  <input
                    className="input-field pwd-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    required
                    minLength={6}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPwd(s => !s)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-register"
                  >
                    <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="register-submit">
                {loading ? <span className="gold-loader" style={{ width: 18, height: 18 }} /> : 'CREATE ACCOUNT'}
              </button>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: 16, lineHeight: 1.6 }}>
                <i className="fa-solid fa-shield-halved" style={{ color: 'var(--gold)', marginRight: 4 }} />
                Encrypted. Confidential. Yours alone.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
